import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { User, Package, Wallet, LogOut, Sparkle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { logout } from "State/AuthSlice/LoginSlice";
import { setLoggedIn } from "State/StateEvents";
import { AppDispatch, RootState } from "State/store";
import { clearWishlist, selectWishlistCount } from "State/WishlistSlice/WishlistSlice";
import { useGetAllActiveAcademicsQuery } from "Services/Admin/ActiveAcademicApiSlice";
import { useGetAuthorsByCategoryQuery } from "Services/Admin/ActiveAuthorApiSlice";
import { useGetBooksCategoryQuery } from "Services/CategoryApiSlice";
import { BookCategory } from "Services/CategoryApiSlice";
import { useGetAllActiveLanguagesQuery } from "Services/Admin/ActiveLanguageApiSlice";
import { useGetAllActiveMerchandisesQuery } from "Services/Admin/ActiveMerchandiseApiSlice";
import SearchBar from "Templates/Template4/Components/Navbar/SearchBar";
import { useEcomConfig } from "hooks/useEcomConfig";
import { clearCartOnLogout } from "State/CartSlice/CartSlice";
import { encodeParam } from "Utilities/urlEncoder";
import { useSearchAnalytics } from "Context/SearchAnalyticsContext";

interface MobileHeaderProps {
  brandName: string;
  onCartOpen?: () => void;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({
  brandName,
  onCartOpen,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [submenu, setSubmenu] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [menuSearch, setMenuSearch] = useState("");

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { queueAnalytics } = useSearchAnalytics();

  const { token } = useSelector((state: RootState) => state.login);
  const { isLoggedIn } = useSelector((state: RootState) => state.stateevents);
  const cartList = useSelector((state: RootState) => state.cart.cartList);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const wishlistCount = useSelector(selectWishlistCount);

  const { showAuthor, showLanguage, showAcademic, showMerchandise, showEvents, showGlossary, showHistory, isLoyaltyPointsEnabled, isWalletEnabled, showSellBooks } = useEcomConfig();

  const { data: categoriesData = [] } = useGetBooksCategoryQuery();

  const { data: languagesData } = useGetAllActiveLanguagesQuery(undefined, {
    skip: !showLanguage,
  });
  const { data: academicsData } = useGetAllActiveAcademicsQuery(undefined, {
    skip: !showAcademic,
  });
  const { data: merchandisesData } = useGetAllActiveMerchandisesQuery(
    undefined,
    {
      skip: !showMerchandise,
    },
  );

  const totalQuantity = cartList.reduce(
    (total, item) => total + (item.quantity || 0),
    0,
  );

  const mappedCategories = categoriesData.map((cat: BookCategory) => {
    const subs =
      cat.subClasses
        ?.map((sub) => ({
          name: sub.subClassName || "Unnamed",
          code: sub.subClassId,
        }))
        ?.filter((sub, index, self) => index === self.findIndex((s) => s.code === sub.code)) || [];

    // Add "None" option at the beginning of subcategories
    const subsWithNone = [{ name: "None", code: "None" }, ...subs];

    return {
      name: cat.categoryName,
      code: cat.categoryId,
      subs: subsWithNone,
    };
  });

  const languages = languagesData?.map((lang) => lang.language) || [];
  const academics = academicsData?.map((aca) => aca.academic) || [];
  const merchandises =
    merchandisesData?.map((merch) => merch.merchandise) || [];

  const menuItems = [
    { name: "Category", path: "/category", enabled: true },
    { name: "Author", path: "/authors", enabled: showAuthor },
    { name: "Language", path: "/language", enabled: showLanguage },
    { name: "Academic", path: "/academic", enabled: showAcademic },
    { name: "Merchandise", path: "/merchandise", enabled: showMerchandise },
    { name: "New Arrival", path: "/new-collections", enabled: true },
    { name: "Bestsellers", path: "/top-sellers?page=1", enabled: true },
    // { name: "Offers", path: "/offers", enabled: showOffers },
    { name: "Events", path: "/events", enabled: showEvents },
    { name: "Gallery", path: "/gallery", enabled: showGlossary },
    { name: "History", path: "/history", enabled: showHistory },
    { name: "Sell Your Book", path: "/sell-with-us", enabled: showSellBooks },
    { name: "Track Order", path: "/track-order", enabled: true },
    { name: "Store Locator", path: "/store-locator", enabled: true },
  ].filter((item) => item.enabled);

  const handleNavigation = (path: string) => {
    window.history.scrollRestoration = "manual";
    window.scrollTo({ top: 0, behavior: "auto" });
    navigate(path);
    setIsMenuOpen(false);
    setSubmenu(null);
    setSelectedCategory(null);
    setIsSearchOpen(false);
    setIsProfileOpen(false);
  };

  const handleCategoryClick = (categoryCode: string, categoryName: string, hasSubs: boolean) => {
    if (hasSubs) {
      setSelectedCategory(categoryName);
    } else {
      queueAnalytics("CATEGORY", categoryName);
      handleNavigation(
        `/books-listing?category=${encodeParam(categoryCode)}`,
      );
    }
  };

  const handleSubcategoryClick = (
    categoryCode: string,
    subcategoryCode: string,
  ) => {
    const subCatValue = subcategoryCode === "None" || subcategoryCode === "none" 
      ? "None" 
      : `${categoryCode}_${subcategoryCode}`;
      
    handleNavigation(
      `/books-listing?category=${encodeParam(
        categoryCode,
      )}&subCategory=${encodeParam(subCatValue)}`,
    );
  };

  const handleBack = () => {
    if (selectedCategory) {
      setSelectedCategory(null);
    } else {
      setSubmenu(null);
    }
  };

  const handleLogout = () => {
    setIsProfileOpen(false);
    setIsMenuOpen(false);

    dispatch(logout());
    dispatch(setLoggedIn(false));
    dispatch(clearCartOnLogout());
    dispatch(clearWishlist());

    navigate("/login");
    window.location.reload();
  };

  const handleMiniCartOpen = () => {
    window.scrollTo({ top: 0, behavior: "auto" });
    if (onCartOpen) {
      onCartOpen();
    } else {
      console.error("onCartOpen is undefined!");
    }
  };

  useEffect(() => {
    setMenuSearch("");
  }, [submenu, selectedCategory]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen) {
      setIsSearchOpen(false);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (isSearchOpen) {
      setIsMenuOpen(false);
    }
  }, [isSearchOpen]);

  useEffect(() => {
    const handleOutsideClick = () => {
      setIsProfileOpen(false);
    };

    if (isProfileOpen) {
      document.addEventListener("click", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [isProfileOpen]);

  // Get the category code for the selected category
  const selectedCategoryCode = selectedCategory
    ? mappedCategories.find((c) => c.name === selectedCategory)?.code ?? null
    : null;

  // Fetch authors for the selected category
  const { data: authorsByCategoryData = [] } = useGetAuthorsByCategoryQuery(
    selectedCategoryCode!,
    { skip: !selectedCategoryCode || !showAuthor }
  );

  // Convert author strings to objects
  const authorsByCategory = authorsByCategoryData.map((author) => ({
    name: author,
  }));

  const authorCategories = categoriesData.map((category: BookCategory) => {
    // For the selected category, use authors fetched from the API
    // Otherwise, use an empty array to show "Hover over a category" message
    const categoryAuthors =
      category.categoryId === selectedCategoryCode ? authorsByCategory : [];

    return {
      name: category.categoryName,
      code: category.categoryId,
      authors: categoryAuthors,
    };
  });

  const searchValue = menuSearch.toLowerCase().trim();

  const renderSubmenuContent = () => {
    switch (submenu) {
      case "Category":
        if (!selectedCategory) {
          return mappedCategories
            .filter((cat) => cat.name.toLowerCase().includes(searchValue))
            .map((category, idx) => (
              <button
                key={idx}
                className="w-full text-left px-4 py-3 rounded-xl text-dark font-medium hover:bg-vintageText hover:bg-opacity-10 transition-all duration-200 flex justify-between items-center group"
                onClick={() =>
                  handleCategoryClick(category.code, category.name, category.subs.length > 0)
                }
              >
                <span className="font-gilroyRegular font-semibold tracking-wider">
                  {category.name}
                </span>
                {category.subs.length > 0 && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="transform group-hover:translate-x-1 transition-transform"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                )}
              </button>
            ));
        } else {
          const category = mappedCategories.find(
            (c) => c.name === selectedCategory,
          );
          return (
            <>
              <button
                className="w-full text-left px-4 py-3 rounded-xl text-dark font-semibold hover:bg-vintageText hover:bg-opacity-10 transition-all duration-200 border-b border-gray-200 mb-2"
                onClick={() => {
                  if (selectedCategory) queueAnalytics("CATEGORY", selectedCategory);
                  handleNavigation(
                    `/books-listing?category=${encodeParam(selectedCategoryCode || "")}`,
                  );
                }}
              >
                All {selectedCategory}
              </button>

              {category?.subs
                .filter((sub) => sub.name.toLowerCase().includes(searchValue))
                .map((sub, idx) => (
                  <button
                    key={idx}
                    className="w-full text-left px-6 py-3 text-dark hover:bg-vintageText hover:bg-opacity-5 transition-all duration-200 font-gilroyRegular"
                    onClick={() => {
                      queueAnalytics("SUB_CATEGORY", sub.name);
                      handleSubcategoryClick(selectedCategoryCode || "", sub.code);
                    }}
                  >
                    {sub.name}
                  </button>
                ))}
            </>
          );
        }

      case "Author":
        if (!showAuthor) return null;

        if (!selectedCategory) {
          return authorCategories
            .filter((cat) => cat.name.toLowerCase().includes(searchValue))
            .map((category, idx) => (
              <button
                key={idx}
                className="w-full text-left px-4 py-3 rounded-xl text-dark font-medium hover:bg-vintageText hover:bg-opacity-10 transition-all duration-200 flex justify-between items-center group"
                onClick={() => setSelectedCategory(category.name)}
              >
                <span className="font-gilroyRegular font-semibold tracking-wider">
                  {category.name}
                </span>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transform group-hover:translate-x-1 transition-transform"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            ));
        } else {
          const category = authorCategories.find(
            (c) => c.name === selectedCategory,
          );

          const filteredAuthors = category?.authors
            .filter((author) =>
              author.name.toLowerCase().includes(searchValue),
            ) || [];

          return (
            <>
              <button
                className="w-full text-left px-4 py-3 rounded-xl text-dark font-semibold hover:bg-vintageText hover:bg-opacity-10 transition-all duration-200 border-b border-gray-200 mb-2"
                onClick={() =>
                  handleNavigation(
                    `/books-listing?category=${encodeParam(
                      selectedCategoryCode || selectedCategory || "",
                    )}`,
                  )
                }
              >
                All {selectedCategory}
              </button>

              {filteredAuthors.length === 0 ? (
                <p className="px-4 py-3 text-dark text-center font-gilroyRegular">
                  No authors for this category
                </p>
              ) : (
                filteredAuthors.map((author, idx) => (
                  <button
                    key={idx}
                    className="w-full text-left px-6 py-3 text-dark hover:bg-vintageText hover:bg-opacity-5 transition-all duration-200 font-gilroyRegular"
                    onClick={() => {
                      queueAnalytics("AUTHOR", author.name);
                      handleNavigation(
                        `/books-listing?category=${encodeParam(
                          selectedCategoryCode || selectedCategory || "",
                        )}&author=${encodeParam(author.name)}`,
                      );
                    }}
                  >
                    {author.name}
                  </button>
                ))
              )}
            </>
          );
        }

      case "Language":
        return (
          <>
            <button
              className="w-full text-left px-4 py-3 rounded-xl text-dark font-semibold hover:bg-vintageText hover:bg-opacity-10 transition-all duration-200 border-b border-gray-200 mb-2"
              onClick={() => handleNavigation("/language")}
            >
              All Languages
            </button>
            {languages.map((language, idx) => (
              <button
                key={idx}
                className="w-full text-left px-4 py-3 text-dark hover:bg-vintageText hover:bg-opacity-5 transition-all duration-200 font-gilroyRegular"
                onClick={() => {
                  queueAnalytics("LANGUAGE", language);
                  handleNavigation(`/books-listing?language=${encodeParam(language)}`);
                }}
              >
                {language}
              </button>
            ))}
          </>
        );

      case "Academic":
        return (
          <>
            <button
              className="w-full text-left px-4 py-3 rounded-xl text-dark font-semibold hover:bg-vintageText hover:bg-opacity-10 transition-all duration-200 border-b border-gray-200 mb-2"
              onClick={() => handleNavigation("/academic")}
            >
              All Academics
            </button>
            {academics.map((academic, idx) => (
              <button
                key={idx}
                className="w-full text-left px-4 py-3 text-dark hover:bg-vintageText hover:bg-opacity-5 transition-all duration-200 font-gilroyRegular"
                onClick={() => {
                  queueAnalytics("ACADEMIC", academic);
                  handleNavigation(`/books-listing?academic=${encodeParam(academic)}`);
                }}
              >
                {academic}
              </button>
            ))}
          </>
        );

      case "Merchandise":
        return (
          <>
            <button
              className="w-full text-left px-4 py-3 rounded-xl text-dark font-semibold hover:bg-vintageText hover:bg-opacity-10 transition-all duration-200 border-b border-gray-200 mb-2"
              onClick={() => handleNavigation("/merchandise")}
            >
              All Merchandise
            </button>
            {merchandises.map((merchandise, idx) => (
              <button
                key={idx}
                className="w-full text-left px-4 py-3 text-dark hover:bg-vintageText hover:bg-opacity-5 transition-all duration-200 font-gilroyRegular"
                onClick={() => {
                  queueAnalytics("MERCHANDISE", merchandise);
                  handleNavigation(`/books-listing?merchandise=${encodeParam(merchandise)}`);
                }}
              >
                {merchandise}
              </button>
            ))}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="sticky top-0 z-50 md:hidden bg-vintageBg px-4 py-3 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsMenuOpen(true)}
              className="p-2 text-vintageText hover:bg-vintageText hover:bg-opacity-10 rounded-lg transition-all duration-200"
              aria-label="Open menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="transform transition-transform duration-300 hover:rotate-90"
              >
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>

            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 text-vintageText hover:bg-vintageText hover:bg-opacity-10 rounded-lg transition-all duration-200"
              aria-label="Search"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="transform transition-transform duration-300 hover:scale-110"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
          </div>

          <Link
            to="/"
            className="flex-1 flex justify-center"
            onClick={() => {
              window.history.scrollRestoration = "manual";
              window.scrollTo({ top: 0, behavior: "auto" });
            }}
          >
            {/* <h1 className="text-xl text-vintageText font-melodramaRegular tracking-wider font-bold">
              {brandName}
            </h1> */}
            <img src="/Moore_market_img.png" alt="Moore Market" className="h-12 w-auto" />
          </Link>

          <div className="flex items-center space-x-2">
            {token && isLoggedIn && (
              <div className="relative p-2 text-vintageText hover:bg-vintageText hover:bg-opacity-10 rounded-lg transition-all duration-200">
                <button
                  onClick={() => handleNavigation("/wishlist")}
                  className="flex items-center"
                  aria-label="Wishlist"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill={wishlistCount > 0 ? "#dc2626" : "none"}
                    stroke={wishlistCount > 0 ? "#dc2626" : "currentColor"}
                    strokeWidth="2"
                    className="lucide lucide-heart text-yellow-600"
                  >
                    <path d="M19 14c1.49-1.46 2-3.45 2-5A5 5 0 0 0 16 4c-1.54 0-3.04.77-4 2-.96-1.23-2.46-2-4-2a5 5 0 0 0-5 5c0 1.55.51 3.54 2 5l7 7Z" />
                  </svg>
                </button>
                {wishlistCount > 0 && (
                  <span
                    className=" absolute
         top-[10px] right-[7px]
        translate-x-1/2 -translate-y-1/2
        bg-red-500 text-white
        text-[10px]
        min-w-[16px] h-[16px]
        flex items-center justify-center
        rounded-full font-semibold"
                  >
                    {wishlistCount}
                  </span>
                )}
              </div>
            )}

            {token && isLoggedIn && (
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setIsProfileOpen((prev) => !prev)}
                  className="p-2 text-vintageText hover:bg-vintageText hover:bg-opacity-10 rounded-lg transition-all duration-200"
                  aria-label="Profile"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="lucide lucide-user text-yellow-600"
                  >
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-vintageBg rounded-xl shadow-lg border border-vintageBorder z-50">
                    <button
                      onClick={() => {
                        handleNavigation("/order-summary");
                        setIsProfileOpen(false);
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2 text-sm text-vintageText hover:bg-vintageText hover:bg-opacity-10"
                    >
                      <Package size={18} />
                      My Orders
                    </button>

                    <button
                      onClick={() => {
                        handleNavigation("/my-profile");
                        setIsProfileOpen(false);
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2 text-sm text-vintageText hover:bg-vintageText hover:bg-opacity-10"
                    >
                      <User size={18} />
                      My Profile
                    </button>

                    {isLoyaltyPointsEnabled && (
                      <button
                        onClick={() => {
                          handleNavigation("/reward-points");
                          setIsProfileOpen(false);
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-vintageText hover:bg-vintageText hover:bg-opacity-10"
                      >
                        <Sparkle size={18} />
                        Rewards
                      </button>
                    )}

                    {isWalletEnabled && (
                      <button
                        onClick={() => {
                          handleNavigation("/wallet");
                          setIsProfileOpen(false);
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-vintageText hover:bg-vintageText hover:bg-opacity-10"
                      >
                        <Wallet size={18} />
                        Wallet
                      </button>
                    )}

                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2 text-sm text-vintageText hover:bg-vintageText hover:bg-opacity-10"
                    >
                      <LogOut size={18} />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}

            {(!token || !isLoggedIn) && (
              <button
                onClick={() => handleNavigation("/login")}
                className="p-2 text-vintageText hover:bg-vintageText hover:bg-opacity-10 rounded-lg transition-all duration-200"
                aria-label="Login"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transform transition-transform duration-300 hover:scale-110"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
            )}

            <div className="relative w-9 h-9 flex items-center justify-center">
              <button
                onClick={handleMiniCartOpen}
                className="text-vintageText hover:bg-vintageText hover:bg-opacity-10 rounded-lg transition-all duration-200 w-full h-full flex items-center justify-center"
                aria-label="Cart"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="lucide lucide-shopping-cart text-yellow-600"
                >
                  <circle cx="8" cy="21" r="1" />
                  <circle cx="19" cy="21" r="1" />
                  <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
                </svg>
              </button>

              {totalQuantity > 0 && (
                <span
                  className="
        absolute
         top-[8px] right-[3px]
        translate-x-1/2 -translate-y-1/2
        bg-red-500 text-white
        text-[10px]
        min-w-[16px] h-[16px]
        flex items-center justify-center
        rounded-full font-semibold
      "
                >
                  {totalQuantity}
                </span>
              )}
            </div>
          </div>
        </div>

        <div
          className={`mt-3 overflow-hidden transition-all duration-300 ease-in-out ${
            isSearchOpen ? "max-h-20 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <SearchBar />
        </div>
      </div>

      <div
        className={`
    fixed top-0 left-0 
    w-full md:w-80
    h-screen
    bg-vintageBg shadow-xl
    z-50
    transform transition-transform duration-500 ease-in-out
    flex flex-col
    ${isMenuOpen ? "translate-x-0" : "-translate-x-full"}
  `}
      >
        <div className="shrink-0 flex justify-between items-center p-6 border-b border-vintageText border-opacity-20">
          <h2 className="text-vintageText font-cardoBold text-xl uppercase tracking-wider">
            Menu
          </h2>
          <button
            onClick={() => setIsMenuOpen(false)}
            className="p-2 text-dark hover:bg-vintageText hover:bg-opacity-10 rounded-full transition-all duration-300 hover:rotate-90"
            aria-label="Close menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <div
          className="
    flex-1
    overflow-y-auto
    overscroll-contain
    p-4
    pb-24
  "
        >
          {(submenu || selectedCategory) && (
            <button
              onClick={handleBack}
              className="mb-4 text-sm text-vintageText hover:underline flex items-center font-gilroyRegular p-2 rounded-lg hover:bg-vintageText hover:bg-opacity-5 transition-all duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-2 transform transition-transform duration-300 hover:-translate-x-1"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
              Back to {selectedCategory ? "Categories" : "Menu"}
            </button>
          )}

          <div className="flex flex-col gap-2">
            {(submenu || selectedCategory) && (
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Search..."
                  value={menuSearch}
                  onChange={(e) => setMenuSearch(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-vintageText bg-white text-dark font-gilroyRegular"
                />
              </div>
            )}
            {!submenu && !selectedCategory ? (
              menuItems.map((item, idx) => (
                <button
                  key={idx}
                  className="w-full text-left px-4 py-3 rounded-xl text-dark font-medium tracking-wide hover:bg-vintageText hover:bg-opacity-10 transition-all duration-300 font-gilroyRegular flex justify-between items-center group hover:translate-x-2"
                  onClick={() => {
                    if (
                      [
                        "Category",
                        "Author",
                        "Language",
                        "Academic",
                        "Merchandise",
                      ].includes(item.name)
                    ) {
                      setSubmenu(item.name);
                    } else {
                      handleNavigation(item.path);
                    }
                  }}
                >
                  <span className="font-semibold">{item.name}</span>
                  {[
                    "Category",
                    "Author",
                    "Language",
                    "Academic",
                    "Merchandise",
                  ].includes(item.name) && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="transform group-hover:translate-x-1 transition-transform"
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  )}
                </button>
              ))
            ) : (
              <div className="animate-fadeIn max-h-[60vh] overflow-y-auto pr-1">
                {renderSubmenuContent()}
              </div>
            )}
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={() => setIsMenuOpen(false)}
        ></div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default MobileHeader;
